import { Toolbar } from "client/Components";
import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { useFollowAction } from "./useFollowAction";
import { ReactionsButton } from "client/Features/Reactions/ReactionsButton/ReactionsButton";
import {
	AnnouncementStatisticsModel,
	registerReaction,
	unregisterReaction,
	updateReaction
} from "client/Api/Hub/Announcements/announcementViewer";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { getHubUrlForAnnouncement } from "../AnnouncementsPage";
import { HubDataSource } from "client/Api/Hub/hub";
import { ReactionKind } from "client/Api/Hub/common";

export type AnnouncementActionsProps = {
	statistics: AnnouncementStatisticsModel;
	onStatisticsRetrieved(statistics: AnnouncementStatisticsModel): void;
	announcementId: number;
	areCommentsEnabled: boolean;
};

export const AnnouncementActions = (props: AnnouncementActionsProps): ReactElement | null => {
	const {
		statistics,
		onStatisticsRetrieved,
		announcementId,
		areCommentsEnabled
	} = props;

	const sendReaction = (reaction: ReactionKind): Promise<AnnouncementStatisticsModel> => {
		if (statistics.currentUserReaction === ReactionKind.NoReaction) {
			return registerReaction(announcementId, reaction);
		}

		if (reaction === statistics.currentUserReaction) {
			return unregisterReaction(announcementId);
		}

		return updateReaction(announcementId, reaction);
	};

	const handleReactionClick = async (reaction: ReactionKind): Promise<void> => {
		try {
			const statistics: AnnouncementStatisticsModel = await sendReaction(reaction);
			onStatisticsRetrieved(statistics);
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			throw e;
		}
	};

	const announcementLink = useMemo(
		(): string => {
			const result: string = getHubUrlForAnnouncement({
				source: HubDataSource.Announcement,
				itemId: announcementId
			});

			return result;
		},
		[announcementId]
	);

	const [
		followIcon,
		followText,
		followClickHandler
	] = useFollowAction(
		announcementId,
		statistics.isCurrentUserFollowingComments,
		onStatisticsRetrieved
	);

	if (!statistics) {
		return null;
	}

	return (
		<Toolbar
			moreButtonKind="secondary"
		>
			<Toolbar.Custom
				responsiveBehavior="always-visible"
				renderNormalMode={() => (
					<ReactionsButton
						userReaction={statistics.currentUserReaction}
						onReactionClick={handleReactionClick}
						kind="secondary"
					/>
				)}
				renderCollapsedMode={() => { throw new Error("Does not support collapse"); }}
			/>

			{areCommentsEnabled && (
				<Toolbar.Button
					kind="secondary"
					trackingId="follow"
					text={localize(followText)}
					icon={followIcon}
					onClick={followClickHandler}
				/>
			)}

			<Toolbar.CopyButton
				value={announcementLink}
				kind="secondary"
				text={localize("_CopyLink")}
				icon="link"
				trackingId="copy-link"
			/>
		</Toolbar>
	);
};

AnnouncementActions.displayName = "AnnouncementActions";
